import { useId, useRef, type ReactNode } from 'react';

import { cx } from '../lib/cx';

export interface TabDef {
  id: string;
  label: string;
  content: ReactNode;
}

export interface TabsProps {
  tabs: readonly TabDef[];
  value: string;
  onChange: (id: string) => void;
  /** Accessible name for the tablist. */
  label: string;
  className?: string;
}

/**
 * A WCAG tablist (a11y §8.6.7): `role="tablist"`/`tab`/`tabpanel`, `aria-selected`,
 * `aria-controls`, roving tabindex and Left/Right/Home/End arrow-key movement.
 */
export function Tabs({ tabs, value, onChange, label, className }: TabsProps): React.JSX.Element {
  const baseId = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const focusTab = (index: number): void => {
    const clamped = (index + tabs.length) % tabs.length;
    const tab = tabs[clamped];
    if (tab === undefined) return;
    onChange(tab.id);
    refs.current[clamped]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent, index: number): void => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusTab(index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusTab(index - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusTab(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusTab(tabs.length - 1);
    }
  };

  const activeTab = tabs.find((t) => t.id === value) ?? tabs[0];

  return (
    <div className={cx('ds-tabs', className)}>
      <div className="ds-tabs__list" role="tablist" aria-label={label}>
        {tabs.map((tab, index) => {
          const selected = tab.id === value;
          return (
            <button
              key={tab.id}
              ref={(node) => {
                refs.current[index] = node;
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              className="ds-tab"
              onClick={() => {
                onChange(tab.id);
              }}
              onKeyDown={(event) => {
                onKeyDown(event, index);
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {activeTab !== undefined ? (
        <div
          role="tabpanel"
          id={`${baseId}-panel-${activeTab.id}`}
          aria-labelledby={`${baseId}-tab-${activeTab.id}`}
          tabIndex={0}
          className="ds-tabs__panel"
        >
          {activeTab.content}
        </div>
      ) : null}
    </div>
  );
}
